class HomepageBannerSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isHovered = false;
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.isPointerDown = false;
    this.hasInitialized = false;
    this.hasInteracted = false;
  }

  connectedCallback() {
    this.slides = Array.from(
      this.querySelectorAll(".homepage-banner-slider__slide"),
    );
    this.prevButton = this.querySelector('[data-action="previous"]');
    this.nextButton = this.querySelector('[data-action="next"]');
    this.indicators = Array.from(
      this.querySelectorAll(".homepage-banner-slider__indicator"),
    );

    if (!this.slides.length) return;

    this.totalSlides = this.slides.length;
    this.autoplayEnabled = this.dataset.autoplay === "true";
    this.stopAutoplayOnInteraction = this.dataset.stopOnInteraction === "true";
    this.autoplay = this.autoplayEnabled && this.totalSlides > 1;
    this.autoplaySpeed = Number(this.dataset.speed || 5) * 1000;
    this.style.setProperty(
      "--homepage-banner-progress-duration",
      `${this.autoplaySpeed}ms`,
    );
    this.currentIndex = this.getInitialIndex();

    this.bindEvents();
    this.goTo(this.currentIndex, false);
    this.setAutoplay();
    this.hasInitialized = true;
  }

  disconnectedCallback() {
    this.clearAutoplay();
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => {
        this.handleInteraction();
        this.goTo(this.currentIndex - 1);
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => {
        this.handleInteraction();
        this.goTo(this.currentIndex + 1);
      });
    }

    this.indicators.forEach((indicator) => {
      indicator.addEventListener("click", () => {
        this.handleInteraction();
        this.goTo(Number(indicator.dataset.slideIndex));
      });
    });

    this.addEventListener("dragstart", (event) => {
      event.preventDefault();
    });

    this.addEventListener("mouseenter", () => {
      if (!this.stopAutoplayOnInteraction) return;
      this.isHovered = true;
      this.clearAutoplay();
    });

    this.addEventListener("mouseleave", () => {
      if (!this.stopAutoplayOnInteraction) return;
      this.isHovered = false;
      this.setAutoplay();
    });

    this.addEventListener("pointerdown", (event) => this.onPointerDown(event), {
      passive: true,
    });
    this.addEventListener("pointerup", (event) => this.onPointerUp(event), {
      passive: true,
    });
    this.addEventListener("pointercancel", () => this.resetPointerTracking());
  }

  getInitialIndex() {
    const activeIndex = this.slides.findIndex((slide) =>
      slide.classList.contains("is-active"),
    );
    return activeIndex >= 0 ? activeIndex : 0;
  }

  normalizeIndex(index) {
    if (index < 0) return this.totalSlides - 1;
    if (index >= this.totalSlides) return 0;
    return index;
  }

  goTo(index, shouldRestartAutoplay = true) {
    const previousIndex = this.currentIndex;
    const targetIndex = this.normalizeIndex(index);
    const direction = this.getDirection(previousIndex, targetIndex);
    this.currentIndex = targetIndex;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.currentIndex;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", String(!isActive));
      slide.tabIndex = isActive ? 0 : -1;
    });

    if (this.prevButton) {
      this.prevButton.classList.toggle("is-inactive", this.currentIndex === 0);
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle(
        "is-inactive",
        this.currentIndex === this.totalSlides - 1,
      );
    }

    this.indicators.forEach((indicator, indicatorIndex) => {
      const isActive = indicatorIndex === this.currentIndex;
      indicator.classList.toggle("slider-counter__link--active", isActive);
      indicator.classList.toggle("is-passed", indicatorIndex < this.currentIndex);
      indicator.classList.remove("is-progressing");
      indicator.classList.remove(
        "is-growing-next",
        "is-shrinking-next",
        "is-growing-prev",
        "is-shrinking-prev",
      );
      if (isActive) {
        indicator.setAttribute("aria-current", "true");
      } else {
        indicator.removeAttribute("aria-current");
      }
    });

    if (
      this.hasInitialized &&
      previousIndex !== this.currentIndex &&
      direction !== "none"
    ) {
      this.applyIndicatorMotion(previousIndex, this.currentIndex, direction);
    }

    this.restartActiveIndicatorProgress();

    if (shouldRestartAutoplay) {
      this.setAutoplay();
    }
  }

  setAutoplay() {
    this.clearAutoplay();
    if (this.stopAutoplayOnInteraction && this.hasInteracted) return;
    if (!this.autoplay || this.isHovered) return;
    this.autoplayTimer = window.setTimeout(() => {
      this.goTo(this.currentIndex + 1);
    }, this.autoplaySpeed);
  }

  clearAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartActiveIndicatorProgress() {
    const activeIndicator = this.indicators[this.currentIndex];
    if (!activeIndicator || !this.autoplay) return;
    activeIndicator.classList.remove("is-progressing");
    void activeIndicator.offsetWidth;
    activeIndicator.classList.add("is-progressing");
  }

  onPointerDown(event) {
    this.isPointerDown = true;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
  }

  onPointerUp(event) {
    if (
      !this.isPointerDown ||
      this.pointerStartX === null ||
      this.pointerStartY === null
    ) {
      return;
    }

    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const threshold = 35;

    if (absX > threshold && absX > absY) {
      this.handleInteraction();
      if (deltaX < 0) {
        this.goTo(this.currentIndex + 1);
      } else {
        this.goTo(this.currentIndex - 1);
      }
    }

    this.resetPointerTracking();
  }

  resetPointerTracking() {
    this.isPointerDown = false;
    this.pointerStartX = null;
    this.pointerStartY = null;
  }

  handleInteraction() {
    this.hasInteracted = true;
    if (this.stopAutoplayOnInteraction) {
      this.clearAutoplay();
      const activeIndicator = this.indicators[this.currentIndex];
      if (activeIndicator) {
        activeIndicator.classList.remove("is-progressing");
      }
    }
  }

  getDirection(previousIndex, targetIndex) {
    if (previousIndex === targetIndex) return "none";
    if (previousIndex === this.totalSlides - 1 && targetIndex === 0)
      return "next";
    if (previousIndex === 0 && targetIndex === this.totalSlides - 1)
      return "prev";
    return targetIndex > previousIndex ? "next" : "prev";
  }

  applyIndicatorMotion(previousIndex, currentIndex, direction) {
    const previousIndicator = this.indicators[previousIndex];
    const currentIndicator = this.indicators[currentIndex];
    if (!previousIndicator || !currentIndicator) return;

    if (direction === "next") {
      previousIndicator.classList.add("is-shrinking-next");
      currentIndicator.classList.add("is-growing-next");
    } else {
      previousIndicator.classList.add("is-shrinking-prev");
      currentIndicator.classList.add("is-growing-prev");
    }

    window.requestAnimationFrame(() => {
      previousIndicator.classList.remove(
        "is-shrinking-next",
        "is-shrinking-prev",
      );
      currentIndicator.classList.remove("is-growing-next", "is-growing-prev");
    });
  }
}

if (!customElements.get("homepage-banner-slider")) {
  customElements.define("homepage-banner-slider", HomepageBannerSlider);
}
